/**
 * Tiny event bus that lets the REST `_client.ts` (which has no React
 * context) signal the UI tree that the user's WorkOS session has
 * expired. A top-level `<SessionExpiredModal>` listens and presents the
 * "Sign back in" UI.
 *
 * Why a bus instead of a React context: `_client.ts` runs both inside
 * and outside React (`useApiClient` exposes the hook flavor; some
 * call sites — link-preview workers, scheduled tasks — call equivalent
 * helpers from non-React code). An event bus is the lowest-coupling
 * surface that works everywhere.
 *
 * Design notes:
 *   - `notifySessionExpired` is throttled — once per 60s — so a burst
 *     of 401s on a multi-request page (e.g. Inbox open with 12 parallel
 *     fetches) raises one modal, not twelve.
 *   - The listener can be subscribed before the runtime has actually
 *     fired the first event (no `late-subscriber miss" semantics like
 *     EventTarget can have when used naively).
 */

const THROTTLE_MS = 60_000;

type Listener = () => void;

class SessionExpiredBus {
  private listeners = new Set<Listener>();
  private lastNotifiedAt = 0;

  /** Subscribe a listener. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Tell every subscriber the session looks expired. Throttled so a
   * fan-out of 401s during one render doesn't queue N modals. The
   * throttle resets after a successful sign-in via `acknowledge()`.
   */
  notify(): void {
    const now = Date.now();
    if (now - this.lastNotifiedAt < THROTTLE_MS) return;
    this.lastNotifiedAt = now;
    for (const fn of this.listeners) {
      try {
        fn();
      } catch (err) {
        // Listener errors must not break the bus for other listeners.

        console.error("[session-expired-bus] listener threw:", err);
      }
    }
  }

  /** Reset the throttle so the next 401 fires immediately. Call after
   *  the user successfully re-authenticates. */
  acknowledge(): void {
    this.lastNotifiedAt = 0;
  }
}

const bus = new SessionExpiredBus();

export function notifySessionExpired(): void {
  bus.notify();
}

export function subscribeSessionExpired(listener: () => void): () => void {
  return bus.subscribe(listener);
}

export function acknowledgeSessionExpired(): void {
  bus.acknowledge();
}

/** Test-only — resets bus state. */
export function _resetSessionExpiredBusForTests(): void {
  bus.acknowledge();
}
